import { useState, useEffect, useCallback, useRef } from 'react';
import { WebSocketService } from '../services/websocketService';
import { WebSocketMessage, UseWebSocketReturn, WebSocketEventHandlers } from '../types/websocket';

export function useWebSocket(
  tenantId: string,
  userId: number,
  token: string,
  eventHandlers?: WebSocketEventHandlers
): UseWebSocketReturn {
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  
  const webSocketServiceRef = useRef<WebSocketService | null>(null);

  // Initialize WebSocket service
  useEffect(() => {
    if (tenantId && userId && token) {
      webSocketServiceRef.current = new WebSocketService(tenantId, userId, token);
      
      // Set up event handlers
      webSocketServiceRef.current.setEventHandlers({
        onConnect: () => {
          setIsConnected(true);
          setConnectionStatus('connected');
          setError(null);
          eventHandlers?.onConnect?.();
        },
        onDisconnect: () => {
          setIsConnected(false);
          setConnectionStatus('disconnected');
          eventHandlers?.onDisconnect?.();
        },
        onError: (error) => {
          setError(`WebSocket error: ${error}`);
          setConnectionStatus('error');
          eventHandlers?.onError?.(error);
        },
        onMessage: (message) => {
          setLastMessage(message);
          eventHandlers?.onMessage?.(message);
        },
        onReconnect: (attempt) => {
          setReconnectAttempts(attempt);
          setConnectionStatus('connecting');
          eventHandlers?.onReconnect?.(attempt);
        },
        onMaxReconnectAttempts: () => {
          setError('Max reconnection attempts reached');
          setConnectionStatus('error');
          eventHandlers?.onMaxReconnectAttempts?.();
        }
      });
    }

    return () => {
      if (webSocketServiceRef.current) {
        webSocketServiceRef.current.disconnect();
        webSocketServiceRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- eventHandlers ref is stable, token/user/tenant drive connection
  }, [tenantId, userId, token]);

  const connect = useCallback(() => {
    if (webSocketServiceRef.current) {
      setConnectionStatus('connecting');
      setError(null);
      webSocketServiceRef.current.connect().catch((err) => {
        setError(`Connection failed: ${err.message}`);
        setConnectionStatus('error');
      });
    }
  }, []);

  const disconnect = useCallback(() => {
    if (webSocketServiceRef.current) {
      webSocketServiceRef.current.disconnect();
      setIsConnected(false);
      setConnectionStatus('disconnected');
    }
  }, []);

  const sendMessage = useCallback((message: Partial<WebSocketMessage>) => {
    if (webSocketServiceRef.current) {
      webSocketServiceRef.current.sendMessage(message);
    }
  }, []);

  // Auto-connect when tenant/user/token are available
  useEffect(() => {
    if (tenantId && userId && token && !isConnected && connectionStatus === 'disconnected') {
      connect();
    }
  }, [tenantId, userId, token, isConnected, connectionStatus, connect]);

  return {
    connect,
    disconnect,
    sendMessage,
    isConnected,
    lastMessage,
    error,
    connectionStatus,
    reconnectAttempts
  };
}
